import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  Signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { matchesSearch } from 'src/app/utils/text-search';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule,
    FormsModule,
  ],
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss',
})
export class AutocompleteComponent<T extends Record<string, any>>
  implements OnChanges
{
  private translateService = inject(TranslateService);
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger?: MatAutocompleteTrigger;

  @Input() data: T[] | Signal<T[]> = []; // Accept array or Signal of objects
  @Input() propertyName!: keyof T; // Property to match
  @Input() optionValuePropertyName?: keyof T; // Property to use as the option value
  @Input() isRequired = false; // Makes field required
  @Input() preSelectedValue: T | string | null = null; // Pre-selected value
  @Input() disabled = false; // Disables input field
  @Input() allowOnlyListValues = false; // Prevents selecting arbitrary typed values
  @Input() clearable = false;
  @Input() multiple = false;
  @Input() selectedValues: T[] = [];
  @Input() invalidListValueMessage = 'Please select a value from the list.';
  @Input() label = this.translateService.instant('auto-complete.search');
  @Input() formatterFn: (item: T | null, value: string) => string = (
    item,
    value,
  ) => value;
  @Output() valueSelected = new EventEmitter<T | string>(); // Emit selected object or string
  @Output() selectedValuesChange = new EventEmitter<T[]>();
  @Output() optionsRequested = new EventEmitter<void>();

  searchText = signal<string>(''); // Use signal for reactivity
  filteredList = signal<T[]>([]);
  lastSelectedText: string = ''; // Store last selected value

  constructor() {
    effect(() => {
      this.filterOptions();
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    const normalizedLabel = this.label.replace(/\*+$/, '');
    this.label = this.isRequired ? `${normalizedLabel}*` : normalizedLabel;

    if (
      changes['data'] ||
      changes['propertyName'] ||
      changes['optionValuePropertyName'] ||
      changes['preSelectedValue'] ||
      changes['selectedValues']
    ) {
      this.filterOptions();
    }

    // preSelectedValue is a classic @Input, not a signal. An effect would not
    // rerun when it changes, so handle subsequent input updates here instead.
    if (changes['preSelectedValue']) {
      this.applyPreSelectedValue();
    }
  }

  private applyPreSelectedValue(): void {
    if (!this.preSelectedValue) {
      this.lastSelectedText = '';
      this.searchText.set('');
      return;
    }

    this.lastSelectedText = this.formatterFn(
      typeof this.preSelectedValue === 'object' ? this.preSelectedValue : null,
      typeof this.preSelectedValue === 'object'
        ? String(this.preSelectedValue[this.propertyName])
        : this.preSelectedValue,
    );
    this.searchText.set(this.lastSelectedText);
  }

  filterOptions() {
    const dataArray = Array.isArray(this.data) ? this.data : this.data();
    const filteredItems = dataArray.filter(
      (item) =>
        matchesSearch(this.searchText(), [String(item[this.propertyName])]) ||
        (this.multiple && this.isOptionSelected(item)),
    );
    this.filteredList.set(
      this.multiple
        ? filteredItems.sort((first, second) => {
            const selectionOrder =
              Number(this.isOptionSelected(second)) -
              Number(this.isOptionSelected(first));
            return (
              selectionOrder ||
              String(first[this.propertyName]).localeCompare(
                String(second[this.propertyName]),
              )
            );
          })
        : filteredItems,
    );
  }

  onBlur() {
    setTimeout(() => {
      if (this.multiple) {
        this.searchText.set('');
        this.filterOptions();
        return;
      }

      if (this.searchText() !== this.lastSelectedText) {
        this.searchText.set(this.lastSelectedText);
      }
    }, 200);
  }

  showOptions(): void {
    this.optionsRequested.emit();
    this.filterOptions();
    if (!this.disabled && this.filteredList().length > 0) {
      this.autocompleteTrigger?.openPanel();
    }
  }

  getOptionValue(item: T): string {
    const valueProperty = this.optionValuePropertyName ?? this.propertyName;
    return String(item[valueProperty]);
  }

  displayOptionValue = (value: string): string => {
    if (this.multiple || !value) {
      return '';
    }

    const dataArray = Array.isArray(this.data) ? this.data : this.data();
    const item = dataArray.find(
      (option) => this.getOptionValue(option) === String(value),
    );
    return item
      ? this.formatterFn(item, String(item[this.propertyName]))
      : String(value);
  };

  isOptionSelected(item: T): boolean {
    const optionValue = this.getOptionValue(item);
    return this.selectedValues.some(
      (selectedItem) => this.getOptionValue(selectedItem) === optionValue,
    );
  }

  hasListValidationError(): boolean {
    return (
      this.allowOnlyListValues &&
      this.searchText().trim().length > 0 &&
      this.filteredList().length === 0
    );
  }

  clearSelection(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.searchText.set('');
    this.lastSelectedText = '';
    this.filterOptions();
    this.valueSelected.emit('');
  }

  selectValue(event: MatAutocompleteSelectedEvent) {
    const selectedValue = String(event.option.value);

    const dataArray = Array.isArray(this.data) ? this.data : this.data();
    const found = dataArray.find(
      (item) =>
        this.getOptionValue(item).toLowerCase() === selectedValue.toLowerCase(),
    );

    if (this.allowOnlyListValues && !found) {
      this.searchText.set(this.lastSelectedText);
      return;
    }

    if (this.multiple && found) {
      const isSelected = this.isOptionSelected(found);
      this.selectedValues = isSelected
        ? this.selectedValues.filter(
            (item) => this.getOptionValue(item) !== this.getOptionValue(found),
          )
        : [...this.selectedValues, found];
      this.searchText.set('');
      this.lastSelectedText = '';
      this.filterOptions();
      this.selectedValuesChange.emit(this.selectedValues);
      event.option.deselect();
      setTimeout(() => this.showOptions());
      return;
    }

    const displayValue = found
      ? this.formatterFn(found, String(found[this.propertyName]))
      : selectedValue;

    this.searchText.set(displayValue);
    this.lastSelectedText = this.searchText();
    this.valueSelected.emit(found || selectedValue);
  }
}
